import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { Magazine } from '@shared/types/magazine';
import { Publication, PublicationResponse } from '@shared/types/publication';
import MagazineApi from '@shared/apis/MagazineApi';
import { ResponseData } from '@shared/types/http';

export default class MagazineStore extends BaseStore {
    magazines: Magazine[] = [];
    magazineDetail: Magazine | null = null;
    magazinePublications: Publication[] = [];
    totalMagazines: number = 0;
    totalMagazinePublications: number = 0;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            magazines: observable,
            magazineDetail: observable,
            magazinePublications: observable,
            totalMagazines: observable,
            totalMagazinePublications: observable,
            fetchMagazines: flow.bound,
            fetchMagazineDetail: flow.bound,
            fetchMagazinePublications: flow.bound
        });
    }

    *fetchMagazines(params?: any) {
        try {
            const res: any = yield this.rootStore.apiStore.call(
                MagazineApi,
                MagazineApi.getMagazines,
                params
            );

            // Handle wrapped response { ok, data: { elements, pagination } }
            if (res?.ok && res.data) {
                this.magazines = res.data.elements || [];
                this.totalMagazines = res.data.pagination?.totalRecord || this.magazines.length;
                return res.data;
            }

            // Handle direct array response
            if (Array.isArray(res)) {
                this.magazines = res;
                this.totalMagazines = res.length;
                return res;
            }

            // Handle case where body is already res.elements (unwrapped success)
            if (res?.elements) {
                this.magazines = res.elements;
                this.totalMagazines = res.pagination?.totalRecord || res.elements.length;
                return res;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *fetchMagazineDetail(id: string) {
        try {
            const res: ResponseData<Magazine> = yield this.rootStore.apiStore.call(
                MagazineApi,
                MagazineApi.getMagazineDetail,
                id,
                {
                    handleErrorBySelf: true
                }
            );

            if (res?.ok) {
                this.magazineDetail = res.data;
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *fetchMagazinePublications(id: string, params?: any) {
        try {
            const res: ResponseData<PublicationResponse> = yield this.rootStore.apiStore.call(
                MagazineApi,
                () => MagazineApi.getMagazinePublications(id, params)
            );

            if (res?.ok) {
                this.magazinePublications = res.data.elements || [];
                this.totalMagazinePublications = res.data.pagination.totalRecord;
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    clean() {
        super.clean();
        this.magazines = [];
        this.magazineDetail = null;
        this.magazinePublications = [];
        this.totalMagazines = 0;
        this.totalMagazinePublications = 0;
    }
}
